import {Store} from "redux";

import {App as ToolbarApp} from "diy/toolbar/App";
import StoreProvider from "diy/redux/StoreProvider";
import globalEvents = require("diy/util/globalEvents");

import RestApi from "./RestApi";
import ReduxApi, {
    readyApi,
    pauseApi,
    resumeApi,
    openCenter,
    closeCenter,
    pushNotificationStates,
    updateNotificationStateList
} from "./ReduxApi";

import ConfigFactory from "./utils/ConfigFactory";
import StateListFactory from "./utils/StateListFactory";
import {NotificationState, NotificationStateList} from "./definitions/Config";

/**
 * Definition of the Redux State for the API
 *    - API state (= status)
 *    - Notification Center state
 *    - Notification list
 */
export interface ApiState {
    isReady: boolean;
    isPaused: boolean;
    isCenterOpen: boolean;
    notificationStateList: NotificationStateList;
}

export class Api {
    private store: Store;
    private toolbar: ToolbarApp;
    private listeners: Array<() => void> = [];

    /**
     * API initialization:
     *    - initializes the Redux store
     *    - loads the initial notifications from the DB
     *    - marks the API as "ready"
     *    - renders React components
     *    - runs live API calls
     *
     * @param {StoreProvider} storeProvider
     * @param {ToolbarApp} toolbar
     */
    init(storeProvider: StoreProvider, toolbar: ToolbarApp) {
        if (!this.isReady()) {

            /** Registers the current Toolbar App */
            this.toolbar = toolbar;

            /** Initializes the Redux store */
            this.initStore(storeProvider);

            /** Loads the notifications from the DB */
            RestApi.getAllNotifications().then((notificationStateList: NotificationStateList) => {

                /** Registers the notifications in the API */
                return this.updateStateList(notificationStateList);

            }).then(() => {

                /** Renders the component through Redux */
                this.render(toolbar);

                /** Informs the API that it's ready */
                this.ready();

                /** Processes the scheduled functions */
                this.process();

            }).catch((reason) => {

                /** Error case: informs the API that its initialization has failed */
                this.logError(
                    "Could not load Notifications: " + reason
                );
            });
        }
    }

    /**
     * Creates or updates a notification state
     * Makes a call to the REST API for same operation in the DB
     *
     * @param   {NotificationState} notificationState
     * @returns {Promise<Api>}
     */
    pushOne(notificationState: NotificationState): Promise<this> {
        let isNew = true;
        let hasChanged = true;
        let type = notificationState.type;

        /** Does the Notification exists? */
        if (this.getState().notificationStateList.has(type)) {
            isNew = false;

            /** Does its state changed? */
            hasChanged = StateListFactory.hasStateChanged(
                this.getState().notificationStateList.get(type),
                notificationState
            );
            if (hasChanged) {
                notificationState = Object.assign({},
                    this.getState().notificationStateList.get(type),
                    notificationState
                );
            }
        }
        if (hasChanged) {
            let notificationStateListUpdate = new Map<number, NotificationState>();
            notificationStateListUpdate.set(type, notificationState);

            return (
                this.pushStateList(notificationStateListUpdate).then(() => {
                    if (isNew) {
                        return RestApi.createNotification(notificationState);
                    } else {
                        return RestApi.updateNotification(notificationState);
                    }
                }).then(() => {
                    return this;

                }).catch((reason) => {
                    this.logError(
                        "Could not push Notification: " + reason
                    );
                    return this;
                })
            );
        }
        return Promise.resolve(this);
    }

    /**
     * "Deletes" a notification state (pushes with "deleted" flag)
     * Makes a call to the REST API for same operation in the DB
     *
     * @param   {number} notificationType
     * @returns {Promise<Api>}
     */
    deleteOne(notificationType: number): Promise<this> {
        if (this.getState().notificationStateList.has(notificationType)) {
            return this.pushOne({
                type: notificationType,
                dismissed: true,
                deleted: true,
                read: true
            });
        }
        return void 0;
    }

    /**
     * Pushes a new state for all existing (or given) notifications
     * with (a) specific property(ies) - read / dismissed / deleted
     * Makes a call to the REST API for same operation in the DB
     * In error cases, informs the API that the operation has failed
     *
     * @params  {Array<string>} property
     * @params  {Array<number>} typesToMark
     * @returns {Promise<Api>}
     */
    markAllAs(properties: Array<string>, typesToMark?: Array<number>): Promise<this> {
        let notificationStateListUpdate = new Map<number, NotificationState>();

        this.getState().notificationStateList.forEach((notificationState: NotificationState) => {
            let hasChanged = false;
            let stateUpdate: NotificationState = {
                type: notificationState.type
            };
            let notificationConfig = ConfigFactory.getConfig(
                notificationState.type
            );

            /** If a restricted list of states to be marked were given, checks if the current states belongs to it */
            if (typeof typesToMark === "undefined"
                || typesToMark.indexOf(notificationState.type) !== -1) {

                /** Get the property to update */
                properties.forEach((property: string) => {
                    switch (property) {
                        case "read":
                            if (!notificationState.read) {
                                stateUpdate.read = true;
                                hasChanged = true;
                            }
                            break;

                        case "dismissed":
                            if (!notificationState.dismissed && notificationConfig.bubbleOptions.closable) {
                                stateUpdate.dismissed = true;
                                hasChanged = true;
                            }
                            break;

                        case "deleted":
                            if (!notificationState.deleted && notificationConfig.cardOptions.closable) {
                                stateUpdate.deleted = true;
                                hasChanged = true;
                            }
                            break;

                        default:
                            break;
                    }
                });

                /** If the property isn't currently set to `true`, adds it to the update list */
                if (hasChanged) {
                    notificationStateListUpdate.set(
                        notificationState.type,
                        Object.assign({},
                            notificationState,
                            stateUpdate
                        )
                    );
                }
            }
        });
        if (notificationStateListUpdate.size > 0) {
            return (
                this.pushStateList(notificationStateListUpdate).then(() => {
                    return RestApi.updateNotifications(notificationStateListUpdate);

                }).then(() => {
                    return this;

                }).catch((reason) => {
                    this.logError(
                        "Could not mark Notifications as '" + properties.join(" / ") + "': " + reason
                    );
                    return this;
                })
            );
        }
        return Promise.resolve(this);
    }

    /**
     * Updates the Notification Center state
     *    - Opens it if it's closed
     *    - Closes it if it's open
     *    - Or force the state if param `open` is given
     *
     * @param {boolean} open
     */
    toggleCenter(open?: boolean) {
        if (typeof open === "undefined") {
            open = !this.getState().isCenterOpen;
        } else {
            if ((open && this.getState().isCenterOpen) || (!open && !this.getState().isCenterOpen)) {
                return;
            }
        }
        if (open) {
            this.openCenter();
        } else {
            this.closeCenter();
        }
    }

    /**
     * READY hook that registers API calls before initialization
     * If the API has already been initialized, the function simply runs it
     *
     * @param {() => void} listener
     */
    schedule(listener: () => void) {
        if (this.getState().isReady) {
            listener();
        } else {
            this.listeners.push(listener);
        }
    }

    /**
     * Runs the API calls registered with the schedule() function
     * Each call is deleted from the queue when processed
     */
    process() {
        while (this.listeners.length > 0) {
            this.listeners.pop()();
        }
    }

    /**
     * Wraps the Redux call of the pauseApi() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {Promise<Api>}
     */
    pause(): Promise<this> {
        return this.store.dispatch(pauseApi())
            .then(() => {
                return this;
            });
    }

    /**
     * Wraps the Redux call of the resumeApi() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {Promise<Api>}
     */
    resume(): Promise<this> {
        return this.store.dispatch(resumeApi())
            .then(() => {
                return this;
            });
    }

    /**
     * Checks if the API is ready
     * (for test purposes)
     *
     * @returns {boolean}
     */
    isReady(): boolean {
        return (this.getState().isReady);
    }

    /**
     * Checks if the API is paused
     * (for test purposes)
     *
     * @returns {boolean}
     */
    isPaused(): boolean {
        return (this.getState().isPaused);
    }

    /**
     * Gets the current Toolbar App
     *
     * @returns {ToolbarApp}
     */
    getToolbar(): ToolbarApp {
        return this.toolbar;
    }

    /**
     * Initializes the Redux State
     *
     * @returns {ApiState}
     */
    getInitialState(): ApiState {
        return {
            isReady: false,
            isPaused: false,
            isCenterOpen: false,
            notificationStateList: new Map<number, NotificationState>()
        };
    }

    /**
     * Wraps the call to get the Redux State
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {ApiState}
     */
    private getState(): ApiState {
        if (typeof this.store === "undefined") {
            return this.getInitialState();
        }
        if (typeof this.store.getState() === "undefined" || !this.store.getState()) {
            return this.getInitialState();
        }
        return ReduxApi.select(this.store.getState());
    }

    /**
     * Initializes the Redux store
     *    - adds our reducer function from the ReduxApi utility class
     *    - adds our store listener from the ReduxApi to know when the state has been changed
     *    - bootstraps the Redux logic and saves the store reference
     *
     * @param storeProvider
     */
    private initStore(storeProvider: StoreProvider) {
        storeProvider.addReducer(ReduxApi.reduce, "notification");
        this.store = storeProvider.getStore();
    }

    /**
     * Registers error cases using the Front-End log
     *
     * @param {string} message
     */
    private logError(message: string) {
        globalEvents.trigger("error.diy-editor", {message: message});
    }

    /**
     * Wraps the call to render the React Components Redux State
     * N.B. This wrapper aims to make the API class more testable
     *
     * @param {ToolbarApp} toolbar
     */
    private render(toolbar: ToolbarApp) {
        ReduxApi.render(this.store, toolbar);
    }

    /**
     * Wraps the Redux call of the readyApi() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {Promise<Api>}
     */
    private ready(): Promise<this> {
        return this.store.dispatch(readyApi())
            .then(() => {
                return this;
            });
    }

    /**
     * Wraps the Redux call of the openCenter() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {Promise<void>}
     */
    private openCenter() {
        return this.markAllAs(["read", "dismissed"]).then(() => {
            this.store.dispatch(openCenter());
        });
    }

    /**
     * Wraps the Redux call of the closeCenter() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @returns {Promise<void>}
     */
    private closeCenter() {
        return this.store.dispatch(closeCenter());
    }

    /**
     * Wraps the Redux call of the pushNotificationState() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @param   {NotificationStateList} notificationStateList
     * @returns {Promise<void>}
     */
    private pushStateList(notificationStateList: NotificationStateList): Promise<void> {
        return this.store.dispatch(pushNotificationStates({
            notificationStateList: notificationStateList
        }));
    }

    /**
     * Wraps the Redux call of the updateNotificationStateList() Action
     * N.B. This wrapper aims to make the API class more testable
     *
     * @param   {NotificationStateList} notificationStateList
     * @returns {Promise<void>}
     */
    private updateStateList(notificationStateList: NotificationStateList): Promise<void> {
        return this.store.dispatch(updateNotificationStateList({
            notificationStateList: notificationStateList
        }));
    }
}

export default new Api();



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/Api.ts
 **/